import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Plus, Clock, User, Lock, Trash2, X, MapPin, Monitor, ClipboardCheck, RotateCcw, Search } from 'lucide-react';
import CancelarConsultaModal from '@/components/CancelarConsultaModal';
import PendenciasStatusBanner from '@/components/PendenciasStatusBanner';
import SharedEventCard from '@/components/EventCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTO_REFRESH_INTERVAL = 5000;

// Categoria especial para bloqueio de horário
const BLOCKED_CATEGORY = 'Horário Bloqueado';

// Cores padrão por categoria (fallback)
const DEFAULT_CATEGORY_COLORS = {
  'Implanon': '#8B5CF6',
  'DIU': '#EC4899',
  'Consulta Clínica': '#3B82F6',
  'Consulta Ginecológica': '#3B82F6',
  'Reposição Hormonal': '#10B981',
  'Reposição de Vitaminas': '#10B981',
  'Papanicolau': '#F59E0B',
  'Horário Bloqueado': '#6B7280',  // Cor cinza para bloqueios
};

// Status colors and badges
const STATUS_STYLES = {
  'agendada': { label: 'Não Confirmada', badge: 'bg-amber-100 text-amber-700 border-amber-300' },
  'confirmada': { label: 'Confirmada', badge: 'bg-green-100 text-green-700 border-green-300' },
  'realizada': { label: 'Realizada', badge: 'bg-gray-100 text-gray-600 border-gray-300' },
  'cancelada': { label: 'Cancelada', badge: 'bg-red-100 text-red-700 border-red-300' },
  'falta': { label: 'Falta', badge: 'bg-amber-100 text-amber-700 border-amber-300' }
};

// Card colors based on tipo: Presencial (pastel blue), Online (pastel indigo), Retorno (pastel rose)
const getCardColor = (consulta) => {
  if (consulta.categoria === 'Horário Bloqueado' || consulta.paciente_nome === 'Horário Bloqueado') return '#9CA3AF';
  if (consulta.tipo_consulta === 'retorno' || consulta.tipo_cobranca === 'retorno_gratuito') return '#E879A0';
  if (consulta.modo_atendimento === 'online') return '#8B8FD6';
  return '#6BA3E8';
};

const dayNamesFull = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// Event Card Component
const EventCard = ({ consulta, compact = false, miniCompact = false, onClick, totalPago }) => (
  <SharedEventCard
    consulta={consulta}
    compact={compact}
    miniCompact={miniCompact}
    onClick={onClick ? () => onClick(consulta) : undefined}
    totalPago={totalPago}
  />
);

// Event Detail Modal Component
const EventDetailModal = ({ consulta, onClose, onStatusChange, onDelete, onMasterDelete, onEdit, medicoNome, isMasterAccess }) => {
  if (!consulta) return null;
  
  const isBlocked = consulta.categoria === BLOCKED_CATEGORY || consulta.paciente_nome === 'Horário Bloqueado';
  const bgColor = getCardColor(consulta);
  const status = STATUS_STYLES[consulta.status] || STATUS_STYLES['agendada'];
  const isRetorno = consulta.tipo_consulta === 'retorno' || consulta.tipo_cobranca === 'retorno_gratuito';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with card color */}
        <div className="p-4 rounded-t-xl" style={{ backgroundColor: bgColor }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isBlocked && <Lock className="w-5 h-5 text-gray-700" />}
              <h3 className="text-gray-900 font-bold text-lg">
                {isBlocked ? 'Horário Bloqueado' : consulta.paciente_nome}
              </h3>
            </div>
            <button onClick={onClose} className="text-gray-600 hover:text-gray-800">
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-gray-600 text-sm">{isBlocked ? 'Bloqueio de agenda' : consulta.categoria}</p>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Status badge + Retorno badge */}
          {!isBlocked && (
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${status.badge}`}>
                {status.label}
              </span>
              {isRetorno && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-300">
                  <RotateCcw className="w-3 h-3" />
                  Retorno
                </span>
              )}
            </div>
          )}
          
          {/* Date & Time */}
          <div className="flex items-center gap-3 text-gray-600">
            <Clock className="w-5 h-5" />
            <div>
              <p className="font-medium">
                {(() => {
                  const [y, m, d] = consulta.data_consulta.split('-');
                  const date = new Date(y, m-1, d);
                  return `${dayNamesFull[date.getDay()]}, ${d}/${m}/${y}`;
                })()}
              </p>
              <p className="text-sm">{consulta.hora_inicio} - {consulta.hora_fim}</p>
            </div>
          </div>
          
          {/* Doctor - Usa nome do calendário configurado (only for non-blocked) */}
          {!isBlocked && (
            <div className="flex items-center gap-3 text-gray-600">
              <User className="w-5 h-5" />
              <span>{medicoNome || consulta.medico_nome || 'Médico não configurado'}</span>
            </div>
          )}
          
          {/* Modo de Atendimento - only for non-blocked */}
          {!isBlocked && consulta.modo_atendimento && (
            <div className="flex items-center gap-3 text-gray-600" data-testid={`detail-modo-${consulta.id}`}>
              {consulta.modo_atendimento === 'online' ? <Monitor className="w-5 h-5" /> : <MapPin className="w-5 h-5" />}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-300 text-slate-600 text-xs font-semibold">
                {consulta.modo_atendimento === 'online' ? <Monitor className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
                {consulta.modo_atendimento === 'online' ? 'Online' : 'Presencial'}
              </span>
            </div>
          )}

          {/* Preventivo - only when authorized */}
          {!isBlocked && consulta.preventivo === 'sim' && (
            <div className="flex items-center gap-3 text-gray-600" data-testid={`detail-preventivo-${consulta.id}`}>
              <ClipboardCheck className="w-5 h-5" />
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-300 text-slate-600 text-xs font-semibold">
                <ClipboardCheck className="w-3.5 h-3.5" />
                Preventivo autorizado
              </span>
            </div>
          )}
          
          {/* Info for blocked */}
          {isBlocked && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                {consulta.observacoes || 'Horário bloqueado através da plataforma'}
              </p>
            </div>
          )}
          
          {/* Value - only for non-blocked */}
          {!isBlocked && consulta.valor && (
            <div className="flex items-center gap-3 text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>R$ {consulta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          
          {/* Confirmation Info - Only for non-blocked */}
          {!isBlocked && (
            <>
              {consulta.status === 'confirmada' || consulta.data_confirmacao ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium text-green-700">Informações de Confirmação</p>
                  <p className="text-sm text-green-600">
                    <span className="font-medium">Data da Confirmação:</span> {consulta.data_confirmacao || '-'}
                  </p>
                  <p className="text-sm text-green-600">
                    <span className="font-medium">Status da Confirmação:</span> {consulta.status_confirmacao || 'CONFIRMADO'}
                  </p>
                  <p className="text-sm text-green-600">
                    <span className="font-medium">Resposta da Confirmação:</span> {consulta.resposta_confirmacao || '-'}
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium text-yellow-700">Informações de Confirmação</p>
                  <p className="text-sm text-yellow-600">
                    <span className="font-medium">Data da Confirmação:</span> Aguardando Confirmação
                  </p>
                  <p className="text-sm text-yellow-600">
                    <span className="font-medium">Status da Confirmação:</span> Aguardando Confirmação
                  </p>
                  <p className="text-sm text-yellow-600">
                    <span className="font-medium">Resposta da Confirmação:</span> Aguardando Confirmação
                  </p>
                </div>
              )}
            </>
          )}
          
          {/* Observations - only for non-blocked */}
          {!isBlocked && consulta.observacoes && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 font-medium mb-1">Observações</p>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{consulta.observacoes}</p>
            </div>
          )}
          
          {/* Actions */}
          <div className="border-t pt-4 space-y-2">
            {isBlocked ? (
              /* Actions for blocked time */
              <Button
                variant="outline"
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => onDelete(consulta.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir Bloqueio
              </Button>
            ) : (
              /* Actions for regular appointments */
              <>
                <p className="text-sm text-gray-500 font-medium">Alterar Status:</p>
                <div className="flex flex-wrap gap-2">
                  {['confirmada', 'realizada', 'falta'].map(s => (
                    <button
                      key={s}
                      onClick={() => onStatusChange(consulta.id, s)}
                      disabled={consulta.status === s}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                        ${consulta.status === s 
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      {STATUS_STYLES[s]?.label || s}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => onEdit(consulta)}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => onDelete(consulta.id)}
                  >
                    Cancelar Consulta
                  </Button>
                </div>
                {isMasterAccess && (
                  <Button
                    variant="outline"
                    className="w-full mt-2 text-red-700 border-red-400 hover:bg-red-100 font-semibold"
                    onClick={() => onMasterDelete(consulta.id)}
                    data-testid="master-excluir-consulta-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir Consulta Definitivamente
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal para Bloquear Horário
const BlockTimeModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    data: '',
    hora_inicio: '',
    hora_fim: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.data || !formData.hora_inicio || !formData.hora_fim) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (formData.hora_inicio >= formData.hora_fim) {
      toast.error('Horário de término deve ser após o horário de início');
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Erro ao bloquear horário:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Gerar opções de horário de 00:00 até 23:30 (de 30 em 30 minutos)
  const timeOptions = [];
  for (let h = 0; h <= 23; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full overflow-hidden box-border"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 'min(384px, calc(100vw - 32px))' }}
      >
        {/* Header */}
        <div className="p-4 rounded-t-xl bg-gray-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-white" />
              <h3 className="text-white font-semibold text-lg">Bloquear Horário</h3>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-white/90 text-sm">Bloqueie um horário na agenda</p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-hidden box-border">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Data <span className="text-red-500">*</span></label>
            <input
              type="date"
              value={formData.data}
              onChange={(e) => setFormData({...formData, data: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-base bg-gray-100 box-border"
              required
              data-testid="input-block-date"
              style={{ 
                WebkitAppearance: 'none', 
                MozAppearance: 'none',
                maxWidth: '100%',
                width: '100%'
              }}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Início <span className="text-red-500">*</span></label>
              <select
                value={formData.hora_inicio}
                onChange={(e) => setFormData({...formData, hora_inicio: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-base"
                data-testid="input-block-start"
                required
              >
                <option value="">Selecione</option>
                {timeOptions.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Término <span className="text-red-500">*</span></label>
              <select
                value={formData.hora_fim}
                onChange={(e) => setFormData({...formData, hora_fim: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-base"
                data-testid="input-block-end"
                required
              >
                <option value="">Selecione</option>
                {timeOptions.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">
              O bloqueio será criado e impedirá agendamentos neste horário.
            </p>
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gray-600 hover:bg-gray-700"
              disabled={submitting}
              data-testid="btn-confirm-block"
            >
              {submitting ? 'Bloqueando...' : 'Bloquear Horário'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================
// DayViewWithGestures: visualização do dia
// Mobile: usa o scroll natural da página + gestos no document
//   - No topo da página + continuar scrollando para cima → dia anterior
//   - No fim da página + continuar scrollando para baixo → próximo dia
//   - Swipe horizontal: esquerda=próximo, direita=anterior
// Desktop: scroll interno normal
// =============================================
function DayViewWithGestures({ currentDate, isToday, dayNamesFull, consultas, handleEventClick, pagamentosResumo, navigatePrev, navigateNext, isMobile }) {
  const lockRef = useRef(false);
  const touchStartRef = useRef(null);

  // Reset locks ao trocar de dia + scroll para o topo
  useEffect(() => {
    lockRef.current = false;
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [currentDate, isMobile]);

  const triggerNav = useCallback((dir) => {
    if (lockRef.current) return;
    lockRef.current = true;
    if (dir > 0) navigateNext(); else navigatePrev();
    setTimeout(() => { lockRef.current = false; }, 600);
  }, [navigateNext, navigatePrev]);

  // Listeners no document (mobile only) — apenas swipe horizontal
  useEffect(() => {
    if (!isMobile) return;

    const onTouchStart = (e) => {
      if (e.touches?.length === 1) {
        const t = e.touches[0];
        touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
      }
    };

    const onTouchEnd = (e) => {
      const start = touchStartRef.current;
      if (!start) return;
      const t = e.changedTouches?.[0];
      if (!t) { touchStartRef.current = null; return; }
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      const elapsed = Date.now() - start.time;

      // Apenas swipe horizontal forte (mais horizontal que vertical, e rápido)
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5 && elapsed < 600) {
        triggerNav(dx < 0 ? 1 : -1);
      }
      touchStartRef.current = null;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, triggerNav]);

  return (
    <div className="flex-1 bg-white rounded-xl border overflow-visible flex flex-col relative">
      {/* Day Header */}
      <div className={`p-4 border-b flex-shrink-0 ${isToday(currentDate) ? 'bg-blue-50' : ''}`}>
        <p className="text-sm text-gray-500">{dayNamesFull[currentDate.getDay()]}</p>
        <p className="text-3xl font-bold text-gray-900">{currentDate.getDate()}</p>
      </div>

      {/* Indicador de swipe horizontal (mobile) */}
      {isMobile && (
        <div className="flex items-center justify-between px-4 py-2 text-[10px] text-gray-400 bg-gray-50/60 border-b select-none">
          <span className="flex items-center gap-1" onClick={() => triggerNav(-1)}>
            <ChevronLeft className="w-3 h-3" />
            Dia anterior
          </span>
          <span className="text-gray-300 italic">deslize para trocar de dia</span>
          <span className="flex items-center gap-1" onClick={() => triggerNav(1)}>
            Próximo dia
            <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      )}

      {/* Day Events — sem scroll interno no mobile (usa o scroll natural da página) */}
      <div
        className={isMobile
          ? "p-4 space-y-5"
          : "p-4 space-y-5 overflow-y-auto flex-1 max-h-[60vh]"
        }
      >
        {consultas.length > 0 ? (
          consultas.map(consulta => (
            <EventCard
              key={consulta.id}
              consulta={consulta}
              onClick={handleEventClick}
              totalPago={pagamentosResumo[consulta.id]}
            />
          ))
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Nenhuma consulta agendada</p>
          </div>
        )}
      </div>
    </div>
  );
}

function AgendaPage({ user, onLogout, showConfig }) {  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [consultas, setConsultas] = useState([]);
  const [procedimentos, setProcedimentos] = useState([]);
  const [procedimentoCores, setProcedimentoCores] = useState({});
  const [loading, setLoading] = useState(true);
  // Detect mobile (< sm = 640px) — sempre força 'day' no mobile
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [viewMode, setViewMode] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 640) ? 'day' : 'week');
  // Forçar mode 'day' quando virar mobile
  useEffect(() => {
    if (isMobile && viewMode !== 'day') setViewMode('day');
  }, [isMobile, viewMode]);
  const isMasterAccess = user?.is_master_access === true;
  
  // Initialize currentDate from URL parameter or default to today
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('data');
    if (dateParam) {
      const parsedDate = new Date(dateParam + 'T12:00:00');
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    return new Date();
  });
  
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [medicoNome, setMedicoNome] = useState('');
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [consultaParaCancelar, setConsultaParaCancelar] = useState(null);
  const [pendenciasOnSuccessRef, setPendenciasOnSuccessRef] = useState(null);
  const [pendenciasRefreshTick, setPendenciasRefreshTick] = useState(0);
  // Dia selecionado/expandido na visão semana (default: dia atual)
  const [selectedWeekDay, setSelectedWeekDay] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });
  const previousDataRef = useRef(null);
  const pendingToastRef = useRef(null);
  const consultasRequestRef = useRef(0);
  
  // Patient search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchPatientName, setSearchPatientName] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPatients, setSearchPatients] = useState([]);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const searchRef = useRef(null);

  // Pagamentos resumo (badge financeira)
  const [pagamentosResumo, setPagamentosResumo] = useState({});
  
  // Check for pending toast from navigation (e.g., after creating a consultation)
  useEffect(() => {
    if (location.state?.toast) {
      // Add new or updated consultation to local state immediately
      if (location.state.newConsulta) {
        setConsultas(prev => [...prev, location.state.newConsulta]);
        previousDataRef.current = null;
        toast.success(location.state.toast);
      } else if (location.state.updatedConsulta) {
        setConsultas(prev => prev.map(c => 
          c.id === location.state.updatedConsulta.id ? location.state.updatedConsulta : c
        ));
        previousDataRef.current = null;
        toast.success(location.state.toast);
      } else {
        pendingToastRef.current = location.state.toast;
      }
      // Clear the state to prevent re-triggering
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, []);
  
  // Clear URL parameter after reading it (optional - keeps URL clean)
  useEffect(() => {
    if (searchParams.get('data')) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Carrega o nome do médico baseado no calendário configurado
  const loadMedicoNome = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/google/calendars`);
      if (response.data.selected_calendar_id && response.data.calendars) {
        const selectedCalendar = response.data.calendars.find(
          c => c.id === response.data.selected_calendar_id
        );
        if (selectedCalendar) {
          // O nome do calendário geralmente é o nome do médico
          setMedicoNome(selectedCalendar.summary || '');
        }
      }
    } catch (error) {
      console.log('Calendário não configurado');
    }
  }, []);

  // Carrega procedimentos para obter as cores
  const loadProcedimentos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/procedimentos`);
      setProcedimentos(response.data);
      // Cria mapa de cores por nome do procedimento
      const cores = {};
      response.data.forEach(p => {
        cores[p.nome] = p.cor;
      });
      setProcedimentoCores(cores);
    } catch (error) {
      console.error('Erro ao carregar procedimentos:', error);
    }
  }, []);

  const getAgendaDateRange = useCallback(() => {
    if (viewMode === 'day') {
      const dia = formatDateKey(currentDate);
      return { data_inicio: dia, data_fim: dia };
    }
    if (viewMode === 'week') {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { data_inicio: formatDateKey(start), data_fim: formatDateKey(end) };
    }
    return {};
  }, [currentDate, viewMode]);

  const loadConsultas = useCallback(async (silent = false) => {
    const requestId = consultasRequestRef.current + 1;
    consultasRequestRef.current = requestId;
    try {
      const params = getAgendaDateRange();
      const consultasRes = await axios.get(`${API}/consultas`, { params });
      if (requestId !== consultasRequestRef.current) return;
      const consultaIds = (consultasRes.data || []).map(c => c.id).filter(Boolean).slice(0, 500);
      const pagamentosRes = consultaIds.length > 0
        ? await axios.get(`${API}/pagamentos/resumo`, { params: { consulta_ids: consultaIds.join(',') } }).catch(() => ({ data: {} }))
        : { data: {} };
      if (requestId !== consultasRequestRef.current) return;
      const newDataHash = JSON.stringify(consultasRes.data.map(c => ({ id: c.id, status: c.status, updated_at: c.updated_at })));
      
      if (previousDataRef.current !== newDataHash) {
        previousDataRef.current = newDataHash;
        setConsultas(consultasRes.data);
      }
      setPagamentosResumo(pagamentosRes.data || {});
      
      // Show pending toast after data is loaded
      if (pendingToastRef.current) {
        toast.success(pendingToastRef.current);
        pendingToastRef.current = null;
      }
    } catch (error) {
      console.error('Erro ao carregar consultas:', error);
      if (!silent) toast.error('Erro ao carregar consultas');
    } finally {
      if (loading) setLoading(false);
    }
  }, [loading, getAgendaDateRange]);

  // Search patient consultations
  const handleSearchPatient = async () => {
    const term = searchTerm.trim();
    if (!term) {
      handleClearSearch();
      return;
    }
    setSearchLoading(true);
    setShowPatientDropdown(false);
    try {
      const response = await axios.get(`${API}/consultas`, { params: { busca_paciente: term, limit: 800 } });
      const all = response.data || [];
      const filtered = all.filter(c =>
        c.paciente_nome?.toLowerCase().includes(term.toLowerCase())
      );

      // Group by unique patient name
      const patientsMap = {};
      filtered.forEach(c => {
        const name = c.paciente_nome || 'Sem nome';
        if (!patientsMap[name]) {
          patientsMap[name] = { nome: name, paciente_id: c.paciente_id, telefone: c.paciente_telefone, consultas: [] };
        }
        patientsMap[name].consultas.push(c);
      });
      const patients = Object.values(patientsMap).sort((a, b) => b.consultas.length - a.consultas.length);

      if (patients.length === 0) {
        setSearchResults([]);
        setSearchPatientName(term);
        setIsSearching(true);
        setSearchPatients([]);
      } else if (patients.length === 1) {
        // Single patient, go directly to results
        selectPatient(patients[0]);
      } else {
        // Multiple patients, show dropdown
        setSearchPatients(patients);
        setShowPatientDropdown(true);
      }
    } catch {
      toast.error('Erro ao buscar consultas');
    } finally {
      setSearchLoading(false);
    }
  };

  const selectPatient = (patient) => {
    const sorted = [...patient.consultas].sort((a, b) => {
      const dateA = a.data_consulta || '';
      const dateB = b.data_consulta || '';
      return dateB.localeCompare(dateA);
    });
    setSearchResults(sorted);
    setSearchPatientName(patient.nome);
    setIsSearching(true);
    setShowPatientDropdown(false);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSearchPatientName('');
    setIsSearching(false);
    setSearchPatients([]);
    setShowPatientDropdown(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearchPatient();
    if (e.key === 'Escape') { setShowPatientDropdown(false); }
  };

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (val === '') handleClearSearch();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowPatientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadMedicoNome();
    loadProcedimentos();
    loadConsultas();
    const intervalId = setInterval(() => loadConsultas(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [loadConsultas, loadProcedimentos, loadMedicoNome]);

  // Helper para obter cor do procedimento (com busca parcial)
  const getProcedimentoCor = (categoria) => {
    if (!categoria) return '#6B7280';
    
    // Primeiro tenta match exato
    if (procedimentoCores[categoria]) return procedimentoCores[categoria];
    if (DEFAULT_CATEGORY_COLORS[categoria]) return DEFAULT_CATEGORY_COLORS[categoria];
    
    // Busca parcial: verifica se a categoria contém algum procedimento ou vice-versa
    const categoriaLower = categoria.toLowerCase();
    for (const [nome, cor] of Object.entries(procedimentoCores)) {
      const nomeLower = nome.toLowerCase();
      // Verifica se um contém o outro (ex: "Protocolo de Reposição de Vitaminas" contém "Reposição de Vitaminas")
      if (categoriaLower.includes(nomeLower) || nomeLower.includes(categoriaLower)) {
        return cor;
      }
    }
    
    // Fallback para cores padrão com busca parcial
    for (const [nome, cor] of Object.entries(DEFAULT_CATEGORY_COLORS)) {
      const nomeLower = nome.toLowerCase();
      if (categoriaLower.includes(nomeLower) || nomeLower.includes(categoriaLower)) {
        return cor;
      }
    }
    
    // Cor padrão cinza se nada encontrado
    return '#6B7280';
  };

  // Helpers
  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const formatDateBR = (dateStr) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const getConsultasForDate = (dateKey) => {
    return consultas
      .filter(c => c.data_consulta === dateKey && c.status !== 'cancelada')
      .sort((a, b) => (a.hora_inicio || '00:00').localeCompare(b.hora_inicio || '00:00'));
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
    // Após mudar de semana: se a nova semana contém hoje, selecionar hoje;
    // caso contrário, NENHUM dia fica selecionado (todas as colunas com largura igual)
    const newWeekStart = new Date(newDate);
    newWeekStart.setDate(newWeekStart.getDate() - newWeekStart.getDay());
    const newWeekEnd = new Date(newWeekStart);
    newWeekEnd.setDate(newWeekEnd.getDate() + 6);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ws = new Date(newWeekStart);
    ws.setHours(0, 0, 0, 0);
    const we = new Date(newWeekEnd);
    we.setHours(23, 59, 59, 999);
    if (today >= ws && today <= we) {
      setSelectedWeekDay(formatDateKey(today));
    } else {
      setSelectedWeekDay(null);
    }
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedWeekDay(formatDateKey(today));
  };

  const handleStatusChange = async (consultaId, newStatus) => {
    // Save previous state for rollback
    const previousConsultas = consultas;
    const previousSelected = selectedConsulta;
    
    // Optimistic: update UI immediately
    const optimisticConsulta = consultas.find(c => c.id === consultaId);
    if (optimisticConsulta) {
      const updatedOptimistic = { ...optimisticConsulta, status: newStatus };
      setConsultas(prev => prev.map(c => c.id === consultaId ? updatedOptimistic : c));
      setSelectedConsulta(updatedOptimistic);
      previousDataRef.current = null;
      toast.success('Status atualizado!');
    }
    
    // Call API in background
    try {
      const response = await axios.patch(`${API}/consultas/${consultaId}/status?status=${newStatus}`);
      // Replace optimistic data with real API response (includes confirmation fields, etc.)
      const realConsulta = response.data;
      setConsultas(prev => prev.map(c => c.id === consultaId ? realConsulta : c));
      if (selectedConsulta?.id === consultaId) {
        setSelectedConsulta(realConsulta);
      }
      previousDataRef.current = null;
    } catch (error) {
      // Rollback on failure
      setConsultas(previousConsultas);
      setSelectedConsulta(previousSelected);
      previousDataRef.current = null;
      toast.error('Erro ao atualizar status');
      console.error('Erro ao atualizar status:', error);
    }
  };

  const handleDelete = async (consultaId) => {
    const consulta = consultas.find(c => c.id === consultaId);
    const isBlocked = consulta?.categoria === BLOCKED_CATEGORY || consulta?.paciente_nome === 'Horário Bloqueado';
    
    if (isBlocked) {
      // Bloqueios: excluir via DELETE (permitido para qualquer usuario)
      if (window.confirm('Excluir bloqueio de horário?')) {
        setConsultas(prev => prev.filter(c => c.id !== consultaId));
        setSelectedConsulta(null);
        previousDataRef.current = null;
        toast.success('Bloqueio excluído!');
        
        try {
          await axios.delete(`${API}/consultas/${consultaId}`);
        } catch (error) {
          if (consulta) {
            setConsultas(prev => [...prev, consulta]);
            previousDataRef.current = null;
          }
          toast.error('Erro ao excluir bloqueio');
          console.error('Erro ao excluir bloqueio:', error);
        }
      }
    } else {
      // Consultas reais: abrir modal de cancelamento
      const alvo = consultas.find(c => c.id === consultaId);
      if (alvo) {
        setSelectedConsulta(null);
        setConsultaParaCancelar(alvo);
      }
    }
  };

  const handleCancelledFromModal = (updatedConsulta) => {
    setConsultas(prev => prev.map(c => c.id === updatedConsulta.id ? { ...c, ...updatedConsulta, status: 'cancelada' } : c));
    previousDataRef.current = null;
    const wr = updatedConsulta?.whatsapp_result;
    // (toast já foi disparado pelo modal; apenas evitar duplicação de stats)
    void wr;
    // Refresh pendências banner
    setPendenciasRefreshTick(t => t + 1);
    if (pendenciasOnSuccessRef) {
      try { pendenciasOnSuccessRef(); } catch {}
      setPendenciasOnSuccessRef(null);
    }
  };

  const handleCancelarFromBanner = (consulta, refreshCb) => {
    setPendenciasOnSuccessRef(() => refreshCb);
    setConsultaParaCancelar(consulta);
  };

  const handleAbrirFinanceiroFromBanner = (consulta) => {
    // Navegar para a página de detalhes onde o módulo financeiro está visível
    navigate(`/consulta/${consulta.id}?focus=financeiro`);
  };

  const handleBannerStatusChange = () => {
    // Quando o banner aplica Realizada/Falta direto, recarrega consultas da agenda
    loadConsultas(true);
  };

  const handleMasterDelete = async (consultaId) => {
    const consulta = consultas.find(c => c.id === consultaId);
    const confirmMsg = `ATENÇÃO: Excluir permanentemente esta consulta?\n\nPaciente: ${consulta?.paciente_nome || 'N/A'}\nStatus: ${consulta?.status || 'N/A'}\n\nEsta ação é irreversível e afetará o controle de retornos.`;
    
    if (window.confirm(confirmMsg)) {
      // Optimistic: remove from UI
      setConsultas(prev => prev.filter(c => c.id !== consultaId));
      setSelectedConsulta(null);
      previousDataRef.current = null;
      
      try {
        await axios.delete(`${API}/consultas/${consultaId}`);
        toast.success('Consulta excluída permanentemente!');
      } catch (error) {
        // Revert on failure
        if (consulta) {
          setConsultas(prev => [...prev, consulta]);
          previousDataRef.current = null;
        }
        toast.error(error.response?.data?.detail || 'Erro ao excluir consulta');
        console.error('Erro ao excluir:', error);
      }
    }
  };

  const handleBlockTime = async (formData) => {
    // Create optimistic entry with temp ID
    const tempId = `temp-block-${Date.now()}`;
    const optimisticBlock = {
      id: tempId,
      data_consulta: formData.data,
      hora_inicio: formData.hora_inicio,
      hora_fim: formData.hora_fim,
      categoria: BLOCKED_CATEGORY,
      paciente_nome: 'Horário Bloqueado',
      status: 'agendada',
      observacoes: 'Horário bloqueado através da plataforma'
    };
    
    // Optimistic: add to UI immediately
    setConsultas(prev => [...prev, optimisticBlock]);
    setShowBlockModal(false);
    previousDataRef.current = null;
    toast.success('Horário bloqueado com sucesso!');
    
    // Call API in background
    try {
      const response = await axios.post(`${API}/consultas/block-time`, {
        data_consulta: formData.data,
        hora_inicio: formData.hora_inicio,
        hora_fim: formData.hora_fim
      });
      // Replace temp entry with real API response
      setConsultas(prev => prev.map(c => c.id === tempId ? response.data : c));
      previousDataRef.current = null;
    } catch (error) {
      // Rollback: remove temp entry
      setConsultas(prev => prev.filter(c => c.id !== tempId));
      previousDataRef.current = null;
      toast.error(error.response?.data?.detail || 'Erro ao bloquear horário');
    }
  };

  const handleEdit = (consulta) => {
    navigate('/nova-consulta', { state: { consulta } });
    setSelectedConsulta(null);
  };

  const handleEventClick = (consulta) => {
    // Navegar para a página de detalhes da consulta
    navigate(`/consulta/${consulta.id}`);
  };

  const weekDays = getWeekDays(currentDate);
  // Se selectedWeekDay não pertence à semana visível, redefine como today (se estiver na semana) ou null
  useEffect(() => {
    if (selectedWeekDay === null) return;
    const weekKeys = weekDays.map(d => formatDateKey(d));
    if (!weekKeys.includes(selectedWeekDay)) {
      const today = new Date();
      const todayKey = formatDateKey(today);
      if (weekKeys.includes(todayKey)) {
        setSelectedWeekDay(todayKey);
      } else {
        setSelectedWeekDay(null);
      }
    }
  }, [currentDate, selectedWeekDay, weekDays]);
  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const monthNamesShort = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
      <div className="h-full flex flex-col" data-testid="agenda-page">
        {/* Banner de pendências de status */}
        <PendenciasStatusBanner
          key={pendenciasRefreshTick}
          onCancelarConsulta={handleCancelarFromBanner}
          onAbrirFinanceiro={handleAbrirFinanceiroFromBanner}
          onChange={handleBannerStatusChange}
        />
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 lg:gap-3 mb-4">
          <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0 flex-wrap">
            {/* Navigation */}
            <div className="flex items-center gap-1 lg:gap-1.5">
              <button
                onClick={goToToday}
                className="px-2.5 lg:px-3 py-1.5 text-xs lg:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Hoje
              </button>
              <button
                onClick={() => viewMode === 'week' ? navigateWeek(-1) : navigateDay(-1)}
                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => viewMode === 'week' ? navigateWeek(1) : navigateDay(1)}
                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Current Period */}
            <h2 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 whitespace-nowrap">
              {viewMode === 'week' ? (
                `${monthNames[weekDays[0].getMonth()]} ${weekDays[0].getFullYear()}`
              ) : (
                `${currentDate.getDate()} ${monthNamesShort[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              )}
            </h2>
          </div>
          
          <div className="flex items-center gap-2 lg:flex-nowrap">
            {/* Patient Search */}
            <div className="relative flex-1 lg:flex-initial min-w-0" ref={searchRef}>
              <div className="flex items-center gap-0">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Buscar consulta por paciente"
                  className="flex-1 lg:flex-initial min-w-0 px-3 py-1.5 text-sm border-2 border-blue-500 border-r-0 rounded-l-lg bg-blue-50 outline-none lg:w-56 placeholder:text-blue-300 placeholder:text-xs"
                  data-testid="search-patient-input"
                />
                <button
                  onClick={handleSearchPatient}
                  disabled={searchLoading}
                  className="px-3 py-1.5 text-sm font-semibold text-white bg-blue-500 hover:bg-blue-600 border-2 border-blue-500 rounded-r-lg transition-colors flex-shrink-0"
                  data-testid="search-patient-btn"
                >
                  {searchLoading ? '...' : 'Buscar'}
                </button>
              </div>

              {/* Dropdown de múltiplos pacientes */}
              {showPatientDropdown && searchPatients.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 sm:right-auto sm:w-96 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden" data-testid="patient-dropdown">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-sm text-gray-600"><strong className="text-gray-900">{searchPatients.length} pacientes</strong> encontrados para &quot;{searchTerm.trim()}&quot;</p>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {searchPatients.map((patient, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectPatient(patient)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-b-0 text-left"
                        data-testid={`patient-option-${idx}`}
                      >
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{patient.nome}</p>
                          {patient.telefone && <p className="text-xs text-gray-400 mt-0.5">{patient.telefone}</p>}
                        </div>
                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{patient.consultas.length} consulta{patient.consultas.length !== 1 ? 's' : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* View Mode Toggle + Bloquear Horário */}
            <div className="hidden sm:flex bg-gray-100 rounded-lg p-1">
              {['week', 'day', ...(isMasterAccess ? ['list'] : [])].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                    ${viewMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {mode === 'week' ? 'Semana' : mode === 'day' ? 'Dia' : 'Lista'}
                </button>
              ))}
              {/* Separador */}
              <div className="w-px bg-gray-300 mx-1"></div>
              {/* Botão Bloquear Horário - mesmo estilo dos view modes */}
              <button
                onClick={() => setShowBlockModal(true)}
                className="px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors text-gray-600 hover:text-gray-900 hover:bg-white flex items-center gap-1 whitespace-nowrap"
                data-testid="btn-bloquear-horario"
              >
                <Lock className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">Bloquear Horário</span>
                <span className="xl:hidden">Bloquear</span>
              </button>
            </div>
            
            {/* Mobile: sem toggle, agenda fica fixada em "Dia" */}
            
            {/* Mobile Bloquear Horário - apenas ícone */}
            <button
              onClick={() => setShowBlockModal(true)}
              className="sm:hidden flex items-center justify-center p-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg border border-gray-300 flex-shrink-0"
              data-testid="btn-bloquear-horario-mobile"
              aria-label="Bloquear horário"
            >
              <Lock className="w-4 h-4" />
            </button>
            
            {/* Botão Nova Consulta - mesmo estilo no mobile e desktop */}
            <Button 
              onClick={() => navigate('/nova-consulta')} 
              className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap flex-shrink-0 px-3 sm:px-4"
              data-testid="btn-nova-consulta"
              aria-label="Nova consulta"
            >
              <Plus className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Nova Consulta</span>
            </Button>
          </div>
        </div>

        {/* Search Results or Calendar Views */}
        {isSearching ? (
          <div className="flex-1 bg-white rounded-xl border overflow-hidden max-w-full" data-testid="search-results">
            {/* Results Header */}
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100">
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">Consultas de {searchPatientName}</h3>
                <p className="text-xs sm:text-sm text-gray-400">{searchResults.length} consulta{searchResults.length !== 1 ? 's' : ''} encontrada{searchResults.length !== 1 ? 's' : ''}</p>
              </div>
              <button
                onClick={handleClearSearch}
                className="flex items-center gap-1.5 px-3 sm:px-5 py-2 sm:py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-sm transition-colors flex-shrink-0 text-xs sm:text-sm whitespace-nowrap"
                data-testid="btn-voltar-agenda"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Voltar para Agenda</span>
                <span className="sm:hidden">Voltar</span>
              </button>
            </div>

            {searchResults.length > 0 ? (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto max-w-full">
                  <table className="w-full" data-testid="search-results-table">
                    <thead>
                      <tr className="bg-gray-50 border-b-2 border-gray-100">
                        <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">Data</th>
                        <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">Horário</th>
                        <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">Procedimento</th>
                        <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">Atendimento</th>
                        <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const hoje = new Date().toISOString().split('T')[0];
                        const futuras = searchResults.filter(c => c.data_consulta >= hoje);
                        const passadas = searchResults.filter(c => c.data_consulta < hoje);
                        futuras.sort((a, b) => (a.data_consulta || '').localeCompare(b.data_consulta || ''));
                        passadas.sort((a, b) => (b.data_consulta || '').localeCompare(a.data_consulta || ''));

                        return (
                          <>
                            {futuras.length > 0 && (
                              <>
                                <tr><td colSpan="5" className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50/70">Futuras</td></tr>
                                {futuras.map(c => {
                                  const isRetorno = c.tipo_consulta === 'retorno' || c.tipo_cobranca === 'retorno_gratuito';
                                  const statusStyle = STATUS_STYLES[c.status] || STATUS_STYLES['agendada'];
                                  return (
                                    <tr key={c.id} className="hover:bg-gray-50 cursor-pointer border-b border-gray-50" onClick={() => setSelectedConsulta(c)} data-testid={`search-row-${c.id}`}>
                                      <td className="px-4 py-3 text-sm font-semibold text-gray-800">{formatDateBR(c.data_consulta)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-700">{c.hora_inicio} - {c.hora_fim}</td>
                                      <td className="px-4 py-3 text-sm text-gray-700">
                                        <span>{c.categoria}</span>
                                        {isRetorno && <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">Retorno</span>}
                                        {c.preventivo === 'sim' && <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 border border-gray-200">Preventivo</span>}
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${c.modo_atendimento === 'online' ? 'bg-cyan-50 text-cyan-700 border border-cyan-200' : 'bg-blue-50 text-blue-600 border border-blue-200'}`}>
                                          {c.modo_atendimento === 'online' ? 'Online' : 'Presencial'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusStyle.badge}`}>{statusStyle.label}</span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                            {passadas.length > 0 && (
                              <>
                                <tr><td colSpan="5" className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50/70">Passadas</td></tr>
                                {passadas.map(c => {
                                  const isRetorno = c.tipo_consulta === 'retorno' || c.tipo_cobranca === 'retorno_gratuito';
                                  const statusStyle = STATUS_STYLES[c.status] || STATUS_STYLES['agendada'];
                                  return (
                                    <tr key={c.id} className="hover:bg-gray-50 cursor-pointer border-b border-gray-50 opacity-50" onClick={() => setSelectedConsulta(c)} data-testid={`search-row-${c.id}`}>
                                      <td className="px-4 py-3 text-sm font-semibold text-gray-800">{formatDateBR(c.data_consulta)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-700">{c.hora_inicio} - {c.hora_fim}</td>
                                      <td className="px-4 py-3 text-sm text-gray-700">
                                        <span>{c.categoria}</span>
                                        {isRetorno && <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">Retorno</span>}
                                        {c.preventivo === 'sim' && <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 border border-gray-200">Preventivo</span>}
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${c.modo_atendimento === 'online' ? 'bg-cyan-50 text-cyan-700 border border-cyan-200' : 'bg-blue-50 text-blue-600 border border-blue-200'}`}>
                                          {c.modo_atendimento === 'online' ? 'Online' : 'Presencial'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusStyle.badge}`}>{statusStyle.label}</span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="sm:hidden divide-y divide-gray-100">
                  {(() => {
                    const hoje = new Date().toISOString().split('T')[0];
                    const futuras = searchResults.filter(c => c.data_consulta >= hoje);
                    const passadas = searchResults.filter(c => c.data_consulta < hoje);
                    futuras.sort((a, b) => (a.data_consulta || '').localeCompare(b.data_consulta || ''));
                    passadas.sort((a, b) => (b.data_consulta || '').localeCompare(a.data_consulta || ''));
                    const sections = [];
                    if (futuras.length > 0) sections.push({ label: 'Futuras', items: futuras, past: false });
                    if (passadas.length > 0) sections.push({ label: 'Passadas', items: passadas, past: true });

                    return sections.map(section => (
                      <div key={section.label}>
                        <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50">{section.label}</div>
                        {section.items.map(c => {
                          const isRetorno = c.tipo_consulta === 'retorno' || c.tipo_cobranca === 'retorno_gratuito';
                          const statusStyle = STATUS_STYLES[c.status] || STATUS_STYLES['agendada'];
                          return (
                            <div key={c.id} className={`px-4 py-3 ${section.past ? 'opacity-50' : ''}`} onClick={() => setSelectedConsulta(c)}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-gray-800">{formatDateBR(c.data_consulta)} - {c.hora_inicio}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusStyle.badge}`}>{statusStyle.label}</span>
                              </div>
                              <p className="text-sm text-gray-700">{c.categoria}</p>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${c.modo_atendimento === 'online' ? 'bg-cyan-50 text-cyan-700' : 'bg-blue-50 text-blue-600'}`}>
                                  {c.modo_atendimento === 'online' ? 'Online' : 'Presencial'}
                                </span>
                                {isRetorno && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-50 text-purple-700">Retorno</span>}
                                {c.preventivo === 'sim' && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-100 text-gray-600">Preventivo</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Nenhuma consulta encontrada para &quot;{searchPatientName}&quot;</p>
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Calendar Views */}
        {viewMode === 'week' && (
          <div className="flex-1 bg-white rounded-xl border overflow-hidden">
            {/* Week Header */}
            <div
              className="grid border-b"
              style={{
                gridTemplateColumns: weekDays.map(d => formatDateKey(d) === selectedWeekDay ? '3fr' : '1fr').join(' ')
              }}
            >
              {weekDays.map((day, idx) => {
                const dateKey = formatDateKey(day);
                const hasEvents = getConsultasForDate(dateKey).length > 0;
                const today = isToday(day);
                const isSelected = dateKey === selectedWeekDay;
                
                return (
                  <button 
                    key={idx}
                    type="button"
                    onClick={() => setSelectedWeekDay(dateKey)}
                    className={`p-2 text-center border-r last:border-r-0 transition-colors
                      ${isSelected ? 'bg-blue-50' : today ? 'bg-blue-50/40' : 'hover:bg-gray-50'}`}
                  >
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">{dayNames[day.getDay()]}</p>
                    <p className={`text-lg lg:text-xl font-semibold mt-0.5
                      ${isSelected ? 'bg-blue-600 text-white w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center mx-auto' : 'text-gray-900'}
                    `}>
                      {day.getDate()}
                    </p>
                    {hasEvents && !isSelected && (
                      <div className={`w-1.5 h-1.5 rounded-full mx-auto mt-1 ${today ? 'bg-blue-600' : 'bg-gray-400'}`}></div>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Week Body */}
            <div
              className="grid min-h-[400px] sm:min-h-[500px]"
              style={{
                gridTemplateColumns: weekDays.map(d => formatDateKey(d) === selectedWeekDay ? '3fr' : '1fr').join(' ')
              }}
            >
              {weekDays.map((day, idx) => {
                const dateKey = formatDateKey(day);
                const dayConsultas = getConsultasForDate(dateKey);
                const today = isToday(day);
                const isSelected = dateKey === selectedWeekDay;
                
                return (
                  <div 
                    key={idx}
                    onClick={() => !isSelected && setSelectedWeekDay(dateKey)}
                    className={`border-r last:border-r-0 ${isSelected ? 'p-2.5 space-y-3' : 'p-1 space-y-1.5 cursor-pointer hover:bg-gray-50/60'} overflow-y-auto
                      ${isSelected ? 'bg-blue-50/30' : today ? 'bg-blue-50/20' : ''}`}
                  >
                    {dayConsultas.map(consulta => (
                      <EventCard 
                        key={consulta.id} 
                        consulta={consulta} 
                        compact={isSelected}
                        miniCompact={!isSelected}
                        onClick={isSelected ? handleEventClick : () => setSelectedWeekDay(dateKey)}
                        totalPago={pagamentosResumo[consulta.id]}
                      />
                    ))}
                    {dayConsultas.length === 0 && (
                      <p className="text-[10px] text-gray-400 text-center py-3 hidden sm:block">
                        {isSelected ? 'Sem consultas' : '—'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {viewMode === 'day' && (
          <DayViewWithGestures
            currentDate={currentDate}
            isToday={isToday}
            dayNamesFull={dayNamesFull}
            consultas={getConsultasForDate(formatDateKey(currentDate))}
            handleEventClick={handleEventClick}
            pagamentosResumo={pagamentosResumo}
            navigatePrev={() => navigateDay(-1)}
            navigateNext={() => navigateDay(1)}
            isMobile={isMobile}
          />
        )}

        {viewMode === 'list' && (
          <div className="flex-1 bg-white rounded-xl border overflow-hidden">
            <div className="divide-y max-h-[70vh] overflow-y-auto">
              {consultas.length > 0 ? (
                [...consultas]
                  .sort((a, b) => {
                    const dateA = new Date(`${a.data_consulta}T${a.hora_inicio}`);
                    const dateB = new Date(`${b.data_consulta}T${b.hora_inicio}`);
                    return dateA - dateB;
                  })
                  .map(consulta => {
                    const procCor = getProcedimentoCor(consulta.categoria);
                    const status = STATUS_STYLES[consulta.status] || STATUS_STYLES['agendada'];
                    const [y, m, d] = consulta.data_consulta.split('-');
                    
                    return (
                      <div
                        key={consulta.id}
                        onClick={() => handleEventClick(consulta)}
                        className="flex items-stretch hover:bg-gray-50 cursor-pointer transition-colors border-l-[6px] border-l-transparent"
                      >
                        {/* Color Bar */}
                        <div className="w-1.5 flex-shrink-0" style={{ backgroundColor: procCor }}></div>
                        
                        {/* Content */}
                        <div className="flex-1 p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full ${status.dot}`}></span>
                                <h3 className="font-semibold text-gray-900 truncate">
                                  {consulta.paciente_nome}
                                </h3>
                              </div>
                              <p className="text-sm" style={{ color: procCor }}>{consulta.categoria}</p>
                              {consulta.modo_atendimento && (
                                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 text-[10px] font-semibold">
                                  {consulta.modo_atendimento === 'online' ? <Monitor className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                                  {consulta.modo_atendimento === 'online' ? 'Online' : 'Presencial'}
                                </span>
                              )}
                              {consulta.preventivo === 'sim' && (
                                <span className="inline-flex items-center gap-1 mt-1 ml-1 px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 text-[10px] font-semibold">
                                  <ClipboardCheck className="w-3 h-3" />
                                  Preventivo
                                </span>
                              )}
                            </div>
                            
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-medium text-gray-900">
                                {d}/{m}
                              </p>
                              <p className="text-sm text-gray-500">
                                {consulta.hora_inicio}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Nenhuma consulta encontrada</p>
                </div>
              )}
            </div>
          </div>
        )}
        </>
        )}

        {/* Event Detail Modal */}
        {selectedConsulta && (
          <EventDetailModal 
            consulta={selectedConsulta} 
            onClose={() => setSelectedConsulta(null)}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            onMasterDelete={handleMasterDelete}
            onEdit={handleEdit}
            medicoNome={medicoNome}
            isMasterAccess={isMasterAccess}
          />
        )}
        
        {/* Block Time Modal */}
        {showBlockModal && (
          <BlockTimeModal 
            onClose={() => setShowBlockModal(false)}
            onSubmit={handleBlockTime}
          />
        )}

        {/* Cancelar Consulta Modal (compartilhado) */}
        {consultaParaCancelar && (
          <CancelarConsultaModal
            consulta={consultaParaCancelar}
            onClose={() => setConsultaParaCancelar(null)}
            onCancelled={handleCancelledFromModal}
          />
        )}
      </div>
    </Layout>
  );
}

export default AgendaPage;
